import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { ReactSortable } from "react-sortablejs";
import { toast } from "react-hot-toast";
import {
  HiOutlineArrowLeft,
  HiOutlineAcademicCap,
  HiOutlineArchiveBox,
  HiOutlineArrowsUpDown,
  HiOutlineChevronDown,
  HiOutlineChevronUp,
} from "react-icons/hi2";

import Loader from "@/components/loader";
import api from "@/services/api";

export default function Organize() {
  const navigate = useNavigate();
  const [careerPaths, setCareerPaths] = useState([]);
  const [allCourses, setAllCourses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [collapsedPaths, setCollapsedPaths] = useState({});

  useEffect(() => {
    fetchAll();
  }, []);

  async function fetchAll() {
    try {
      setLoading(true);
      const [pathsRes, coursesRes] = await Promise.all([api.post("/career_path/search", {}), api.post("/training/search", {})]);

      if (!pathsRes.ok || !coursesRes.ok) throw new Error("Failed to fetch data");

      setCareerPaths(pathsRes.data);
      setAllCourses(coursesRes.data);
    } catch (e) {
      toast.error("Error loading data");
    } finally {
      setLoading(false);
    }
  }

  // Find which courses are NOT assigned to any career path
  const assignedCourseIds = new Set();
  careerPaths.forEach((path) => {
    (path.trainings || []).forEach((t) => assignedCourseIds.add(t.training_id));
  });

  const unassignedCourses = allCourses
    .filter((c) => !assignedCourseIds.has(c._id))
    .map((c) => ({
      id: c._id,
      training_id: c._id,
      training_title: c.title,
      training_description: c.description,
      training_image: c.image,
      training_category: c.category,
      training_position: 0,
    }));

  // Build sortable lists for each career path
  const getPathCourses = (path) => {
    return (path.trainings || [])
      .slice()
      .sort((a, b) => (a.training_position || 0) - (b.training_position || 0))
      .map((t) => ({ ...t, id: t.training_id }));
  };

  // Save a career path's trainings to the API
  const saveCareerPath = useCallback(
    async (pathId, trainings) => {
      try {
        setSaving(true);
        const reindexed = trainings.map((t, i) => ({ ...t, training_position: i }));
        const { ok, data } = await api.put(`/career_path/${pathId}`, { trainings: reindexed });
        if (!ok) throw new Error();
        // Update local state
        setCareerPaths((prev) => prev.map((p) => (p._id === pathId ? { ...p, trainings: data.trainings || reindexed } : p)));
      } catch (e) {
        toast.error("Failed to save");
        fetchAll(); // Reload on error
      } finally {
        setSaving(false);
      }
    },
    [careerPaths],
  );

  // Handle moving a course from one place to another
  const handleDrop = async (targetPathId, newList) => {
    if (targetPathId === "unassigned") {
      // Can't drop into "unassigned" via sortable — we handle removal differently
      return;
    }

    const targetPath = careerPaths.find((p) => p._id === targetPathId);
    if (!targetPath) return;

    const newTrainings = newList.map((item, i) => ({
      training_id: item.training_id,
      training_title: item.training_title,
      training_description: item.training_description,
      training_image: item.training_image,
      training_category: item.training_category,
      training_position: i,
    }));

    // Optimistically update UI
    setCareerPaths((prev) => prev.map((p) => (p._id === targetPathId ? { ...p, trainings: newTrainings } : p)));
  };

  // Persist after drag ends
  const handleDragEnd = async (targetPathId) => {
    if (targetPathId === "unassigned") return;

    const targetPath = careerPaths.find((p) => p._id === targetPathId);
    if (!targetPath) return;

    await saveCareerPath(targetPathId, targetPath.trainings);

    // Also save any other paths that may have lost a course
    for (const path of careerPaths) {
      if (path._id === targetPathId) continue;
      await saveCareerPath(path._id, path.trainings || []);
    }
  };

  // Remove a course from a career path (move to unassigned)
  const handleRemoveCourse = async (pathId, courseId) => {
    const path = careerPaths.find((p) => p._id === pathId);
    if (!path) return;

    const newTrainings = (path.trainings || []).filter((t) => t.training_id !== courseId);
    setCareerPaths((prev) => prev.map((p) => (p._id === pathId ? { ...p, trainings: newTrainings } : p)));
    await saveCareerPath(pathId, newTrainings);
    toast.success("Course moved to unassigned");
  };

  // Add a course to a career path from unassigned
  const handleAddCourse = async (pathId, course) => {
    const path = careerPaths.find((p) => p._id === pathId);
    if (!path) return;

    const alreadyExists = (path.trainings || []).some((t) => t.training_id === course.training_id);
    if (alreadyExists) return toast.error("Already in this path");

    const newTrainings = [
      ...(path.trainings || []),
      {
        training_id: course.training_id,
        training_title: course.training_title,
        training_description: course.training_description,
        training_image: course.training_image,
        training_category: course.training_category,
        training_position: (path.trainings || []).length,
      },
    ];

    setCareerPaths((prev) => prev.map((p) => (p._id === pathId ? { ...p, trainings: newTrainings } : p)));
    await saveCareerPath(pathId, newTrainings);
    toast.success("Course added");
  };

  const toggleCollapse = (pathId) => {
    setCollapsedPaths((prev) => ({ ...prev, [pathId]: !prev[pathId] }));
  };

  if (loading) return <Loader />;

  const SORTABLE_GROUP = "career-path-courses";

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100">
      <div className="p-6">
        {/* Header */}
        <div className="flex items-center justify-between mb-6 max-w-screen-2xl mx-auto">
          <div className="flex items-center gap-4">
            <button
              onClick={() => navigate("/selecole/career-path")}
              className="inline-flex items-center gap-2 text-gray-600 hover:text-gray-900 transition-colors group">
              <HiOutlineArrowLeft className="w-5 h-5 group-hover:-translate-x-1 transition-transform" />
            </button>
            <div>
              <h1 className="text-2xl font-bold text-gray-900">Course Organizer</h1>
              <p className="text-sm text-gray-600">Drag & drop courses between career paths. Changes save automatically.</p>
            </div>
          </div>
          {saving && (
            <div className="flex items-center gap-2 text-sm text-primary-600 bg-primary-50 px-4 py-2 rounded-full">
              <div className="w-3 h-3 border-2 border-primary-600 border-t-transparent rounded-full animate-spin" />
              Saving...
            </div>
          )}
        </div>

        {/* Kanban Board */}
        <div className="max-w-screen-2xl mx-auto flex gap-4 overflow-x-auto pb-6" style={{ minHeight: "70vh" }}>
          {/* Career Path Columns */}
          {careerPaths.map((path) => {
            const courses = getPathCourses(path);
            const isCollapsed = collapsedPaths[path._id];

            return (
              <div key={path._id} className="flex-shrink-0 w-72 flex flex-col">
                {/* Column Header */}
                <div className="bg-white rounded-t-xl border border-gray-200 border-b-0 p-4">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2 min-w-0">
                      <div className="w-8 h-8 bg-primary-100 rounded-lg flex items-center justify-center flex-shrink-0">
                        <HiOutlineAcademicCap className="w-4 h-4 text-primary-600" />
                      </div>
                      <div className="min-w-0">
                        <h3 className="font-semibold text-gray-900 text-sm truncate">{path.name}</h3>
                        <span className="text-xs text-gray-500">{courses.length} courses</span>
                      </div>
                    </div>
                    <button onClick={() => toggleCollapse(path._id)} className="p-1 hover:bg-gray-100 rounded-md transition-colors">
                      {isCollapsed ? <HiOutlineChevronDown className="w-4 h-4 text-gray-400" /> : <HiOutlineChevronUp className="w-4 h-4 text-gray-400" />}
                    </button>
                  </div>
                </div>

                {/* Course List */}
                {!isCollapsed && (
                  <div className="bg-gray-50 rounded-b-xl border border-gray-200 flex-1 p-2 min-h-[120px]">
                    <ReactSortable
                      list={courses}
                      setList={(newList) => handleDrop(path._id, newList)}
                      group={SORTABLE_GROUP}
                      animation={200}
                      ghostClass="opacity-30"
                      dragClass="shadow-xl"
                      forceFallback={true}
                      fallbackOnBody={true}
                      swapThreshold={0.65}
                      onEnd={() => handleDragEnd(path._id)}
                      className="space-y-2 min-h-[80px]">
                      {courses.map((course, index) => (
                        <CourseChip
                          key={course.training_id}
                          course={course}
                          index={index}
                          onRemove={() => handleRemoveCourse(path._id, course.training_id)}
                        />
                      ))}
                    </ReactSortable>

                    {courses.length === 0 && (
                      <div className="flex items-center justify-center h-20 text-xs text-gray-400 border-2 border-dashed border-gray-200 rounded-lg">
                        Drop courses here
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          })}

          {/* Unassigned Column */}
          <div className="flex-shrink-0 w-72 flex flex-col">
            <div className="bg-amber-50 rounded-t-xl border border-amber-200 border-b-0 p-4">
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 bg-amber-100 rounded-lg flex items-center justify-center">
                  <HiOutlineArchiveBox className="w-4 h-4 text-amber-600" />
                </div>
                <div>
                  <h3 className="font-semibold text-gray-900 text-sm">Unassigned</h3>
                  <span className="text-xs text-gray-500">{unassignedCourses.length} courses</span>
                </div>
              </div>
            </div>

            <div className="bg-amber-50/50 rounded-b-xl border border-amber-200 flex-1 p-2 min-h-[120px]">
              <div className="space-y-2">
                {unassignedCourses.map((course) => (
                  <UnassignedCourseChip key={course.training_id} course={course} careerPaths={careerPaths} onAdd={handleAddCourse} />
                ))}
                {unassignedCourses.length === 0 && (
                  <div className="flex items-center justify-center h-20 text-xs text-gray-400 border-2 border-dashed border-amber-200 rounded-lg">
                    All courses assigned
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function CourseChip({ course, index, onRemove }) {
  return (
    <div className="bg-white rounded-lg border border-gray-200 p-3 cursor-grab active:cursor-grabbing hover:border-primary-300 hover:shadow-sm transition-all group">
      <div className="flex items-start gap-2.5">
        <div className="flex-shrink-0 w-5 h-5 bg-primary-100 text-primary-700 rounded-full flex items-center justify-center text-[10px] font-bold mt-0.5">
          {index + 1}
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-sm font-medium text-gray-900 truncate">{course.training_title}</div>
          {course.training_description && <div className="text-xs text-gray-500 truncate mt-0.5">{course.training_description}</div>}
          {course.training_category && (
            <span className="inline-block mt-1 px-1.5 py-0.5 text-[10px] font-medium bg-blue-50 text-blue-600 rounded">{course.training_category}</span>
          )}
        </div>
        <button
          onClick={(e) => {
            e.stopPropagation();
            onRemove();
          }}
          className="opacity-0 group-hover:opacity-100 p-1 hover:bg-red-50 rounded text-gray-400 hover:text-red-500 transition-all flex-shrink-0"
          title="Remove from path">
          ×
        </button>
      </div>
    </div>
  );
}

function UnassignedCourseChip({ course, careerPaths, onAdd }) {
  const [showMenu, setShowMenu] = useState(false);

  return (
    <div className="relative">
      <div
        className="bg-white rounded-lg border border-amber-200 p-3 hover:border-primary-300 hover:shadow-sm transition-all group cursor-pointer"
        onClick={() => setShowMenu(!showMenu)}>
        <div className="flex items-start gap-2.5">
          <div className="flex-shrink-0 w-5 h-5 bg-amber-100 text-amber-700 rounded-full flex items-center justify-center text-[10px] mt-0.5">
            +
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-sm font-medium text-gray-900 truncate">{course.training_title}</div>
            {course.training_category && (
              <span className="inline-block mt-1 px-1.5 py-0.5 text-[10px] font-medium bg-amber-50 text-amber-600 rounded">{course.training_category}</span>
            )}
          </div>
        </div>
      </div>

      {/* Quick-assign dropdown */}
      {showMenu && (
        <>
          <div className="fixed inset-0 z-10" onClick={() => setShowMenu(false)} />
          <div className="absolute top-full left-0 right-0 mt-1 bg-white rounded-lg border border-gray-200 shadow-lg z-20 py-1 max-h-48 overflow-y-auto">
            <div className="px-3 py-1.5 text-[10px] font-semibold text-gray-400 uppercase tracking-wider">Add to...</div>
            {careerPaths.map((path) => (
              <button
                key={path._id}
                onClick={() => {
                  onAdd(path._id, course);
                  setShowMenu(false);
                }}
                className="w-full text-left px-3 py-2 text-sm text-gray-700 hover:bg-primary-50 hover:text-primary-700 transition-colors truncate">
                {path.name}
              </button>
            ))}
          </div>
        </>
      )}
    </div>
  );
}
